import React, { useEffect, useRef } from 'react';

const STATIC_COLOR = {
  default: { r: 124, g: 58, b: 237 },
  winamax: { r: 249, g: 115, b: 22 },
};

const VIOLET = { r: 124, g: 58, b: 237 };
const ORANGE = { r: 249, g: 115, b: 22 };

const drawStaticFrame = (canvas, ctx, accentR, accentG, accentB) => {
  const W = canvas.width = window.innerWidth;
  const H = canvas.height = window.innerHeight;
  ctx.clearRect(0, 0, W, H);

  const rng = (() => {
    let s = 42;
    return () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  })();

  const nodeCount = 38;
  const nodes = Array.from({ length: nodeCount }, () => ({ x: rng() * W, y: rng() * H, r: rng() * 1.4 + 0.4 }));

  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[j].x - nodes[i].x;
      const dy = nodes[j].y - nodes[i].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 180 || rng() > 0.38) continue;
      const alpha = (1 - dist / 180) * 0.18;
      ctx.strokeStyle = `rgba(${accentR},${accentG},${accentB},${alpha})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(nodes[i].x, nodes[i].y);
      const mid = rng() > 0.5 ? { x: nodes[j].x, y: nodes[i].y } : { x: nodes[i].x, y: nodes[j].y };
      ctx.lineTo(mid.x, mid.y);
      ctx.lineTo(nodes[j].x, nodes[j].y);
      ctx.stroke();
    }
  }

  nodes.forEach(n => {
    const bright = n.r > 1.5;
    if (bright) {
      const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 10);
      grad.addColorStop(0, `rgba(${accentR},${accentG},${accentB},0.25)`);
      grad.addColorStop(1, `rgba(${accentR},${accentG},${accentB},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(n.x, n.y, 10, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = `rgba(${accentR},${accentG},${accentB},${bright ? 0.7 : 0.3})`;
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
    ctx.fill();
  });

  for (let i = 0; i < 6; i++) {
    const x1 = rng() * W, y1 = rng() * H;
    const len = 120 + rng() * 200;
    const angle = Math.PI / 4 + (rng() - 0.5) * 0.8;
    const x2 = x1 + Math.cos(angle) * len, y2 = y1 + Math.sin(angle) * len;
    const grad = ctx.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, `rgba(${accentR},${accentG},${accentB},0)`);
    grad.addColorStop(0.5, `rgba(${accentR},${accentG},${accentB},0.12)`);
    grad.addColorStop(1, `rgba(${accentR},${accentG},${accentB},0)`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  const hx = W - 60, hy = H - 60;
  [40, 65, 90].forEach((r, i) => {
    ctx.strokeStyle = `rgba(${accentR},${accentG},${accentB},${0.12 - i * 0.03})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(hx, hy, r, -Math.PI * 0.8, -Math.PI * 0.1);
    ctx.stroke();
  });
};

const Background = ({ theme = 'default' }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;

    if (theme === 'launcher') {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const onResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
      const onMouseMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
      const onMouseLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

      window.addEventListener('resize', onResize);
      window.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseleave', onMouseLeave);

      const NODE_COUNT = 62;
      const nodes = Array.from({ length: NODE_COUNT }, () => {
        const isOrange = Math.random() > 0.5;
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.45,
          r: Math.random() * 1.5 + 0.5,
          color: isOrange ? ORANGE : VIOLET,
          bright: Math.random() > 0.62,
        };
      });

      const MAX_DIST = 155;
      const REPEL_R = 140;

      const animate = () => {
        const w = canvas.width;
        const h = canvas.height;
        const mouse = mouseRef.current;

        ctx.clearRect(0, 0, w, h);

        // Subtle cursor glow
        if (mouse.x > -500) {
          const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 110);
          glow.addColorStop(0, 'rgba(180, 100, 255, 0.07)');
          glow.addColorStop(0.5, 'rgba(255, 130, 60, 0.04)');
          glow.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, 110, 0, Math.PI * 2);
          ctx.fill();
        }

        // Physics update
        for (const n of nodes) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < REPEL_R * REPEL_R && d2 > 0) {
            const dist = Math.sqrt(d2);
            const t = (REPEL_R - dist) / REPEL_R;
            const force = t * t * 1.4;
            n.vx += (dx / dist) * force;
            n.vy += (dy / dist) * force;
          }

          n.vx = n.vx * 0.955 + (Math.random() - 0.5) * 0.025;
          n.vy = n.vy * 0.955 + (Math.random() - 0.5) * 0.025;

          const spd = Math.sqrt(n.vx * n.vx + n.vy * n.vy);
          if (spd > 2.8) { n.vx = (n.vx / spd) * 2.8; n.vy = (n.vy / spd) * 2.8; }

          n.x += n.vx;
          n.y += n.vy;

          if (n.x < 0) { n.x = 0; n.vx = Math.abs(n.vx) * 0.6; }
          else if (n.x > w) { n.x = w; n.vx = -Math.abs(n.vx) * 0.6; }
          if (n.y < 0) { n.y = 0; n.vy = Math.abs(n.vy) * 0.6; }
          else if (n.y > h) { n.y = h; n.vy = -Math.abs(n.vy) * 0.6; }
        }

        // Draw connections
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[j].x - nodes[i].x;
            const dy = nodes[j].y - nodes[i].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > MAX_DIST) continue;

            const alpha = (1 - dist / MAX_DIST) * 0.26;
            const { r: r1, g: g1, b: b1 } = nodes[i].color;
            const { r: r2, g: g2, b: b2 } = nodes[j].color;

            const grad = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            grad.addColorStop(0, `rgba(${r1},${g1},${b1},${alpha})`);
            grad.addColorStop(1, `rgba(${r2},${g2},${b2},${alpha})`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 0.85;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            // Deterministic L-shape (no flicker)
            if ((i + j) % 2 === 0) {
              ctx.lineTo(nodes[j].x, nodes[i].y);
            } else {
              ctx.lineTo(nodes[i].x, nodes[j].y);
            }
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }

        // Draw nodes
        for (const n of nodes) {
          const { r, g, b } = n.color;
          if (n.bright) {
            const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 12);
            grad.addColorStop(0, `rgba(${r},${g},${b},0.3)`);
            grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(n.x, n.y, 12, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.fillStyle = `rgba(${r},${g},${b},${n.bright ? 0.85 : 0.42})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fill();
        }

        animFrame = requestAnimationFrame(animate);
      };

      animate();

      return () => {
        cancelAnimationFrame(animFrame);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseleave', onMouseLeave);
      };
    } else {
      // Static themed background
      const { r: accentR, g: accentG, b: accentB } = STATIC_COLOR[theme] || STATIC_COLOR.default;
      const draw = () => drawStaticFrame(canvas, ctx, accentR, accentG, accentB);
      draw();
      window.addEventListener('resize', draw);
      return () => window.removeEventListener('resize', draw);
    }
  }, [theme]);

  return (
    <>
      <canvas
        ref={canvasRef}
        id="bg-canvas"
        style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
      />
      <div
        id="bg-orbs"
        className={
          theme === 'winamax' ? 'winamax-orbs' :
          theme === 'launcher' ? 'launcher-orbs' : ''
        }
      >
        <span /><span /><span />
      </div>
      <div id="scanlines" />
    </>
  );
};

export default Background;
